import { useState, useRef, useEffect } from "react";
import type { RuntimeModel } from "../runtime/modelCatalog";
import type { OocPresetDocument } from "@private-fiction/story-core";
import { WorkspaceDialog } from "./WorkspaceDialog";
import { ModelPicker } from "./ModelPicker";

export const frequentOocPresets: OocPresetDocument[] = [
  { id: "ooc-1", title: "เพิ่มความระทึก", text: "/ooc ขอเพิ่มความกดดันและอันตรายในฉากนี้ให้มากขึ้น" },
  { id: "ooc-2", title: "เน้นบทสนทนา", text: "/ooc เน้นให้ตัวละครตอบโต้กันทางวาจาอย่างมีลูกล่อลูกชน" },
  { id: "ooc-3", title: "ลงรายละเอียดสิ่งแวดล้อม", text: "/ooc บรรยายสภาพแวดล้อม กลิ่น แสง และเสียงรอบตัวให้ชัดเจนยิ่งขึ้น" },
  { id: "ooc-4", title: "ความลับ/ปริศนา", text: "/ooc ค่อยๆ เปิดเผยร่องรอยหรือเงื่อนงำใหม่อย่างแนบเนียน" },
];

interface StoryComposerProps {
  sessionId: string;
  actorName: string;
  draftInput: string;
  onDraftChange: (value: string) => void;
  onSend: (text: string) => void;
  onContinue: () => void;
  onRegenerate: (instruction: string) => void;
  onRewind: () => void;
  onStop: () => void;
  generating: boolean;
  generationStatus: string;
  models: RuntimeModel[];
  selectedModel: string;
  onSelectModel: (model: RuntimeModel) => void;
  customOoc: OocPresetDocument[];
  onCustomOocChange: (items: OocPresetDocument[]) => void;
  companionBusy?: boolean;
  onGenerateScene?: () => void;
}

export function StoryComposer({
  sessionId,
  actorName,
  draftInput,
  onDraftChange,
  onSend,
  onContinue,
  onRegenerate,
  onRewind,
  onStop,
  generating,
  generationStatus,
  models,
  selectedModel,
  onSelectModel,
  customOoc,
  onCustomOocChange,
  companionBusy = false,
  onGenerateScene,
}: StoryComposerProps) {
  const [value, setValue] = useState(draftInput);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [oocOpen, setOocOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [regenerateInstruction, setRegenerateInstruction] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [newOocOpen, setNewOocOpen] = useState(false);
  const [newOocTitle, setNewOocTitle] = useState("");
  const [newOocText, setNewOocText] = useState("");
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [selectedMediaModel, setSelectedMediaModel] = useState<string>(() => {
    try {
      return localStorage.getItem("pf-selected-image-model") || "gpt-image-2.5-flare";
    } catch {
      return "gpt-image-2.5-flare";
    }
  });
  const [imageGenEnabled, setImageGenEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem("pf-image-gen-enabled") !== "false";
    } catch {
      return true;
    }
  });

  const handleSelectImageModel = (modelId: string) => {
    setSelectedMediaModel(modelId);
    try { localStorage.setItem("pf-selected-image-model", modelId); } catch {}
  };

  const handleToggleImageGen = (enabled: boolean) => {
    setImageGenEnabled(enabled);
    try { localStorage.setItem("pf-image-gen-enabled", enabled ? "true" : "false"); } catch {}
  };
  const rootRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setValue(draftInput);
  }, [sessionId]);

  useEffect(() => {
    const input = textareaRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(expanded ? 320 : 180, Math.max(expanded ? 160 : 48, input.scrollHeight))}px`;
  }, [value, expanded]);

  useEffect(() => {
    if (value === draftInput) return;
    const timer = window.setTimeout(() => onDraftChange(value), 350);
    return () => window.clearTimeout(timer);
  }, [value, draftInput, onDraftChange]);

  useEffect(() => {
    if (!toolsOpen && !regenerateOpen && !modelPickerOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setToolsOpen(false);
        setOocOpen(false);
        setRegenerateOpen(false);
        setNewOocOpen(false);
        setModelPickerOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setToolsOpen(false);
        setOocOpen(false);
        setRegenerateOpen(false);
        setNewOocOpen(false);
        setModelPickerOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [toolsOpen, regenerateOpen, modelPickerOpen]);

  const insertOoc = (text: string) =>
    setValue((current) => [current.trim(), text].filter(Boolean).join("\n"));

  const submit = () => {
    if (generating || companionBusy) return;
    if (!value.trim()) { onContinue(); return; }
    onSend(value.trim());
    setValue("");
    onDraftChange("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) {
      e.preventDefault();
      submit();
    }
  };

  const clearDraft = () => {
    if (value.trim()) {
      setValue("");
      onDraftChange("");
    } else {
      onRewind();
    }
  };

  const allOoc = [
    ...frequentOocPresets,
    ...customOoc,
  ].filter(
    (item, index, items) =>
      items.findIndex((other) => other.text.trim() === item.text.trim()) === index
  );

  return (
    <div className={`craft-composer-wrap ${expanded ? "expanded" : ""}`} ref={rootRef}>
      {/* Popovers */}
      {regenerateOpen && (
        <div className="regenerate-popover frosted-panel">
          <header>
            <div>
              <strong>สร้างคำตอบใหม่</strong>
              <small>คำตอบเดิมจะยังอยู่เป็น Variant</small>
            </div>
            <button type="button" onClick={() => setRegenerateOpen(false)}>×</button>
          </header>
          <div className="regenerate-chips">
            {allOoc.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() =>
                  setRegenerateInstruction((current) =>
                    [current.trim(), preset.text].filter(Boolean).join("\n")
                  )
                }
              >
                {preset.title}
              </button>
            ))}
          </div>
          <textarea
            value={regenerateInstruction}
            onChange={(e) => setRegenerateInstruction(e.target.value)}
            placeholder="คำสั่ง High Priority สำหรับรอบนี้ (ไม่บังคับ)"
            rows={4}
          />
          <button
            type="button"
            className="primary"
            disabled={generating}
            onClick={() => {
              onRegenerate(regenerateInstruction);
              setRegenerateInstruction("");
              setRegenerateOpen(false);
            }}
          >
            ↻ สร้างใหม่
          </button>
        </div>
      )}

      {toolsOpen && (
        <div className="story-tools-popover frosted-panel">
          <div className="tools-heading">
            <small>คำสั่งและการควบคุม</small>
          </div>
          <button
            type="button"
            disabled={generating}
            onClick={() => {
              onContinue();
              setToolsOpen(false);
            }}
          >
            ✦ ดำเนินเรื่องต่อ (Auto Continue)
          </button>
          <button
            type="button"
            disabled={generating}
            onClick={() => {
              setRegenerateOpen(true);
              setToolsOpen(false);
            }}
          >
            ↻ สร้างทางเลือกใหม่ (Regenerate)
          </button>
          <button
            type="button"
            onClick={() => {
              onRewind();
              setToolsOpen(false);
            }}
          >
            ↶ ย้อนกลับเทิร์นล่าสุด (Rewind)
          </button>
          <button
            type="button"
            onClick={() => setOocOpen(!oocOpen)}
          >
            ◌ OOC ที่ใช้บ่อย ({allOoc.length}) {oocOpen ? "▴" : "▾"}
          </button>

          {oocOpen && (
            <div className="ooc-presets-tray">
              {allOoc.map((preset) => (
                <div key={preset.id} className="ooc-item-row">
                  <button type="button" onClick={() => insertOoc(preset.text)}>
                    {preset.title}
                  </button>
                  {customOoc.some((item) => item.id === preset.id) && (
                    <button
                      type="button"
                      className="ooc-delete"
                      title="ลบ OOC"
                      onClick={() =>
                        onCustomOocChange(customOoc.filter((item) => item.id !== preset.id))
                      }
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="ooc-new"
                onClick={() => { setToolsOpen(false); setOocOpen(false); setNewOocOpen(true); }}
              >
                ＋ สร้าง OOC ส่วนตัว
              </button>

            </div>
          )}
        </div>
      )}


              {newOocOpen && (
                <WorkspaceDialog title="สร้าง OOC ส่วนตัว" onClose={() => setNewOocOpen(false)}><div className="ooc-editor">
                  <input
                    value={newOocTitle}
                    onChange={(e) => setNewOocTitle(e.target.value)}
                    placeholder="ชื่อทางลัด"
                  />
                  <textarea
                    value={newOocText}
                    onChange={(e) => setNewOocText(e.target.value)}
                    placeholder="ข้อความ OOC"
                    rows={2}
                  />
                  <button
                    type="button"
                    disabled={!newOocTitle.trim() || !newOocText.trim()}
                    onClick={() => {
                      onCustomOocChange([
                        ...customOoc,
                        { id: crypto.randomUUID(), title: newOocTitle.trim(), text: newOocText.trim() },
                      ]);
                      setNewOocTitle("");
                      setNewOocText("");
                      setNewOocOpen(false);
                    }}
                  >
                    บันทึก
                  </button>
                </div></WorkspaceDialog>
              )}
      {/* Generating Status Bar */}
      {generating && (
        <div className="craft-generation-status" role="status">
          <span className="pulsing-dot" />
          <span>{generationStatus || "โลกกำลังตอบกลับ..."}</span>
          <button type="button" className="inline-stop-btn" onClick={onStop}>
            หยุด
          </button>
        </div>
      )}

      {/* One frosted composer surface. */}
      <form
        className="craft-prompt-box frosted-panel"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {/* Top Row: Token indicator + expand chevron */}
        <div className="craft-prompt-header">
          {companionBusy && <small role="status">Movis กำลังปรับข้อมูลโลก…</small>}
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="craft-chevron-btn"
            onClick={() => setExpanded(!expanded)}
            title={expanded ? "ย่อลง" : "ขยายช่องพิมพ์"}
            aria-label={expanded ? "ย่อลง" : "ขยายช่องพิมพ์"}
          >
            {expanded ? "⌄" : "˄"}
          </button>
        </div>

        {/* Center: Clean Textarea */}
        <div className="craft-prompt-textarea-wrap">
          <textarea
            ref={textareaRef}
            className="craft-prompt-textarea"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={generating ? generationStatus || "โลกกำลังตอบกลับ..." : `${actorName} พูดหรือทำอะไร...`}
            rows={expanded ? 6 : 1}
            disabled={generating}
          />
        </div>

        {/* Bottom Row: 4 Icon Buttons on Left, Model Pill & Emerald Send on Right */}
        <div className="craft-prompt-footer">
          {/* Left Action Buttons */}
          <div className="craft-left-actions">
            {/* 1. Tools / OOC + button */}
            <button
              type="button"
              className={`craft-circle-btn ${toolsOpen ? "active" : ""}`}
              onClick={() => setToolsOpen(!toolsOpen)}
              title="คำสั่งและเครื่องมือ"
              aria-label="เปิดเมนูคำสั่งและเครื่องมือ"
            >
              +
            </button>

            {/* Generate the current scene from selected visual fields. */}
            <button
              type="button"
              className="craft-circle-btn"
              onClick={onGenerateScene}
              title="สร้างภาพฉาก"
              aria-label="สร้างภาพฉาก"
            >
              ▧
            </button>

            {/* 3. Clean / Rewind brush button */}
            <button
              type="button"
              className="craft-circle-btn"
              onClick={clearDraft}
              title={value.trim() ? "ล้างข้อความร่าง" : "ย้อนเทิร์นล่าสุด"}
              aria-label={value.trim() ? "ล้างข้อความร่าง" : "ย้อนเทิร์นล่าสุด"}
            >
              🧹
            </button>

          </div>

          {/* Right Controls */}
          <div className="craft-right-actions">
            {/* Unified Model Selector Pill */}
            <ModelPicker
              models={models}
              selectedId={selectedModel}
              onSelect={onSelectModel}
              selectedImageModelId={selectedMediaModel}
              onSelectImageModel={handleSelectImageModel}
              imageGenerationEnabled={imageGenEnabled}
              onToggleImageGeneration={handleToggleImageGen}
            />

            {/* Emerald Circular Play/Send Button */}
            <button
              type={generating ? "button" : "submit"}
              className={`craft-emerald-send-btn ${generating ? "generating" : ""}`}

              disabled={companionBusy && !generating}
              onClick={generating ? onStop : undefined}
              title={generating ? "หยุดการสร้างข้อความ" : value.trim() ? "ส่งข้อความ" : "ดำเนินเรื่องต่อ (Auto Continue)"}
              aria-label={generating ? "หยุดการสร้างข้อความ" : value.trim() ? "ส่งข้อความ" : "ดำเนินเรื่องต่อ (Auto Continue)"}
            >
              {generating ? (
                <span className="stop-square">■</span>
              ) : (
                <svg className="play-triangle-icon" viewBox="0 0 24 24">
                  <polygon points="6 3, 20 12, 6 21" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
